/**
 * TanStack Query hooks for the Azure AI estate board.
 *
 * **Unlike every other board added recently, this one has live data on first
 * paint.** The managed identity already holds `Reader` at the tenant root, so the
 * expected shape of a successful response is `access_state: "ok"` with real
 * numbers — not a ships-dark setup panel. What it shows is not flattering (a
 * 33.74% HTTP 429 rate on one account, a quota at 89.55%, zero alert rules and
 * zero diagnostic settings estate-wide), which is the point.
 *
 * None of these routes fails because Azure is unavailable, unauthorised or
 * throttled: a missing credential, a 403, a throttle and a rejected query are all
 * *fields* on a 200 (`configured` / `access_state` / `status_detail` /
 * `coverage` / `errors`). A thrown error from any hook here therefore means the
 * **NOVA** API is down, not Azure — worth remembering when reading an `error`
 * branch.
 *
 * **The poll interval comes from the payload, not from a constant here.** Every
 * `/ai/*` response carries `cache_ttl_sec` — how long the backend reuses a
 * snapshot before it re-reads Azure — and the routes deliberately disagree.
 * Four of the five are projections of one cached build, so they share its TTL;
 * `/ai/spend` has a much longer one because Cost Management is throttled
 * tenant-wide on Query Processing Units and a dashboard refresh must not spend
 * the estate's cost-query budget. Polling faster than the backend rebuilds just
 * re-serves identical bytes, and a hardcoded interval would silently go wrong the
 * moment someone tunes a TTL. Same reasoning as `./fo.ts` and `./defender.ts`,
 * which read their intervals off the wire for the same reason.
 *
 * **Nothing here is persisted.** `../persist.ts` is a deny-by-default allow-list
 * and `["ai", …]` is deliberately absent from it: these payloads name internal AI
 * accounts, internal MCP hostnames, model deployments and what the estate spends.
 * That does not belong in browser storage.
 *
 * SDK functions are imported from `@/lib/api/generated` rather than the
 * `@/lib/api` barrel, matching every other hook file: the barrel re-exports these
 * hooks, so importing it here would close an import cycle.
 */

import { useQuery } from "@tanstack/react-query";

import {
  getAiCapacity,
  getAiEstate,
  getAiMcpHealth,
  getAiOverview,
  getAiSpend,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Query key for the board's headline payload (KPI strip, composition, findings). */
export const AI_OVERVIEW_KEY = ["ai", "overview"] as const;
/** Query key for the full account inventory. */
export const AI_ESTATE_KEY = ["ai", "estate"] as const;
/** Query key for quota headroom and throttling — the board's most valuable panel. */
export const AI_CAPACITY_KEY = ["ai", "capacity"] as const;
/** Query key for MCP server fleet health. */
export const AI_MCP_KEY = ["ai", "mcp"] as const;
/** Query key for month-to-date spend (its own long TTL — see the module docstring). */
export const AI_SPEND_KEY = ["ai", "spend"] as const;

/**
 * Interval used only before the first response arrives, in ms.
 *
 * Five minutes: shorter than any TTL the board ships with, so the first refetch
 * can never be slower than the backend's own rebuild. After that the payload's
 * own `cache_ttl_sec` takes over.
 */
const FALLBACK_INTERVAL_MS = 300_000;

/**
 * Refetch on the cadence the payload itself declares.
 *
 * Takes the *data* rather than the query so it cannot feed back into TanStack's
 * inference of the query's own data type.
 */
function intervalFromTtl(data: { cache_ttl_sec: number } | undefined): number {
  const ttlSec = data?.cache_ttl_sec;
  return ttlSec != null && ttlSec > 0 ? ttlSec * 1000 : FALLBACK_INTERVAL_MS;
}

/**
 * The board's entry query: the KPI strip, composition by kind/region/type, and
 * the findings list.
 *
 * The KPI numbers lead on quota exhaustion and the HTTP 429 rate rather than the
 * resource census, because those are the numbers that are currently wrong in the
 * estate and nobody was told.
 */
export function useAiOverview() {
  return useQuery({
    queryKey: AI_OVERVIEW_KEY,
    queryFn: async () => {
      const { data } = await getAiOverview();
      // Counts and states only — never spread an estate payload into a log line.
      logger.debug("[ai] overview received", {
        accessState: data?.access_state,
        accounts: data?.summary.accounts_total,
        deployments: data?.summary.deployments_total,
        worstThrottlePct: data?.summary.worst_throttle_pct,
        findings: data?.findings.length ?? 0,
        lowerBound: data?.coverage.is_lower_bound,
        errors: data?.errors.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}

/**
 * Every discovered AI service account, with its deployments, usage and
 * observability.
 *
 * Read `deployments` and `tokens_window` through `usage_state` and never as bare
 * numbers: `0` is a measured zero and a finding, `null` is a read that failed.
 * The capacity board also reads this query, because the per-account throttling
 * rate lives here and both payloads are projections of one cached build — so the
 * two pages cannot disagree, and asking for both costs one backend snapshot.
 */
export function useAiEstate() {
  return useQuery({
    queryKey: AI_ESTATE_KEY,
    queryFn: async () => {
      const { data } = await getAiEstate();
      logger.debug("[ai] estate received", {
        accessState: data?.access_state,
        accounts: data?.accounts.length ?? 0,
        kinds: data?.by_kind.length ?? 0,
        types: data?.by_type.length ?? 0,
        missing: data?.coverage.accounts_missing,
        errors: data?.errors.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}

/**
 * Quota headroom per deployment and the per-account throttling rate.
 *
 * `quotas` is large — on the order of a thousand rows on the live estate, most of
 * them at `0` of their limit — because the payload carries the whole population
 * so the board can count it honestly. Rendering all of it is the caller's problem
 * to *not* do (dashboard rules §1); `summary.quotas_total` is the denominator for
 * the "N of M" caption.
 */
export function useAiCapacity() {
  return useQuery({
    queryKey: AI_CAPACITY_KEY,
    queryFn: async () => {
      const { data } = await getAiCapacity();
      logger.debug("[ai] capacity received", {
        accessState: data?.access_state,
        quotaRows: data?.quotas.length ?? 0,
        quotasTotal: data?.summary.quotas_total,
        quotasExhausted: data?.summary.quotas_exhausted,
        worstUtilisationPct: data?.summary.worst_utilisation_pct,
        deployments: data?.deployments.length ?? 0,
        errors: data?.errors.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}

/**
 * MCP server fleet health: what was identified, out of how many candidates, and
 * what each one's ARM state and HTTP probe say separately.
 *
 * `summary.candidates_considered` is the honesty field — MCP-ness is not an Azure
 * resource type, so a classification rule that stops matching must show up as
 * "0 of 75 candidates" rather than as a clean, empty, reassuring panel.
 */
export function useAiMcpHealth() {
  return useQuery({
    queryKey: AI_MCP_KEY,
    queryFn: async () => {
      const { data } = await getAiMcpHealth();
      logger.debug("[ai] mcp health received", {
        accessState: data?.access_state,
        discovered: data?.summary.servers_discovered,
        candidates: data?.summary.candidates_considered,
        serving: data?.summary.servers_serving,
        probesEnabled: data?.summary.probes_enabled,
        errors: data?.errors.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}

/**
 * Month-to-date spend on the discovered AI estate, by meter category and account.
 *
 * Meter categories are discovered by *grouping*, never filtered by name: AI moved
 * from the `Azure OpenAI` meter to `Foundry Models` / `Foundry Tools`, so a name
 * filter returns zero spend — wrong, and wrong in the flattering direction. The
 * currency is a payload field too (DKK today), so nothing here assumes one.
 */
export function useAiSpend() {
  return useQuery({
    queryKey: AI_SPEND_KEY,
    queryFn: async () => {
      const { data } = await getAiSpend();
      logger.debug("[ai] spend received", {
        accessState: data?.access_state,
        currency: data?.currency,
        subscriptionsAnswered: data?.subscriptions_answered,
        subscriptionsQueried: data?.subscriptions_queried,
        meters: data?.by_meter_category.length ?? 0,
        accounts: data?.by_account.length ?? 0,
        errors: data?.errors.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}
